<template>
  <section class="client-history">
    <card-wrapper id="client-history-card" :hide-close-btn="true" :show-divider="false">
      <m-btn class="client-history__go-back-btn px-6" @click="handleRedirect()">
        <div>
          <v-icon class="fa-light fa-chevron-left" size="12" color="black" />
          <span class="pl-3">{{ 'Volver' }}</span>
        </div>
      </m-btn>
      <section class="client-history__header">
        <div class="client-history__info" v-if="client">
          <h2>{{ client.firstName }} {{ client.lastName }}</h2>
          <span>{{ client.phone | formatPhone }} · {{ client.email }}</span>
        </div>
        <span class="w-20" v-if="!client" />
        <div class="client-history__actions">
          <m-select
            :items="filterItems"
            :placeholder="$t('client-history.list.lbl-filter-by-event')"
            :showSelectedCounter="true"
            :multiple="true"
            :value="selectedEventTypes"
            :disabled="isLoading"
            class="client-history__select"
            @input="value => (selectedEventTypes = value)">
            <template v-slot:prepend-inner>
              <i class="icon-mapit icon-filter-status"></i>
            </template>
          </m-select>
          <m-btn
            hoverTextColor="gray"
            variant="outlined"
            :loading="isLoading"
            :disabled="isLoading"
            @click="handleRedirect('DealerBikesHome')">
            <v-icon dense color="#9a9a9a" class="mr-2">fak fa-light fa-m-motorcycle-street</v-icon>
            {{ $t('buttons.create-demo-bike') }}
          </m-btn>
          <m-btn
            variant="outlined"
            hoverTextColor="gray"
            :loading="isLoading"
            :disabled="isLoading"
            @click="handleLeadDialogOpenClose()">
            <v-icon dense class="mr-2">fa-light fa-bullseye-arrow</v-icon>
            Crear Lead
          </m-btn>
          <m-btn
            variant="outlined"
            class="btn-secondary"
            @click="onCreateOfferMotorbike()"
            :loading="isLoading"
            :disabled="isLoading"
            data-cy="btn-create-offer">
            <v-icon dense class="mr-2">fak fa-light fa-m-file-plus</v-icon>
            {{ $t('title.create-offer') }}
          </m-btn>
        </div>
      </section>
      <v-divider class="mt-4" />
      <client-history-table
        :is-loading="isLoading"
        :table-rows="filteredTableRows"
        :reset-pagination="resetPaginationTrigger"
        @refreshLoansList="refreshLoansList"
        @on-update-lead="reloadData()" />
      <create-lead-dialog
        :is-initially-contact-search-bar-disabled="true"
        :initial-phone-number="client && client.phone"
        :initial-first-name="client && client.firstName"
        :initial-last-name="client && client.lastName"
        :initial-email="client && client.email"
        :phone-number="client && client.phone"
        :address-country="client && client.address.addressCountry"
        :address-region="client && client.address.addressRegion"
        :isDialogOpen="isLeadDialogOpen"
        :is-creating-from-client-history="true"
        @onCreated="reloadData"
        @onClose="handleLeadDialogOpenClose()" />
    </card-wrapper>
  </section>
</template>

<script>
import {mapGetters} from 'vuex'

import DealerBikesApi from '@/api/dealerBikes.api'
import {DealersContactsApi} from '@/api/dealersContacts.api'
import apiOffers from '@/api/offers.api'
import ridoApi from '@/api/rido.api'
import CardWrapper from '@/components/general/CardWrapper.vue'
import CreateLeadDialog from '@/components/offer-manager/CreateLeadDialog.vue'
import {mapLoansToClientHistory} from '@/mappers/dealer-bikes'
import {mapLeadToClientHistory} from '@/mappers/leads'
import {mapOffersToClientHistory} from '@/mappers/offers'
import router from '@/router'
import {ClientHistoryEventTypes} from '@/types'
import {Status} from '@/types/offers'

import ClientHistoryTable from './ClientHistoryTable.vue'

export default {
  name: 'ClientHistory',
  components: {ClientHistoryTable, CreateLeadDialog, CardWrapper},
  data() {
    return {
      resetPaginationTrigger: false,
      client: null,
      tableRows: [],
      isLoading: false,
      isDealerDialogOpen: false,
      isLeadDialogOpen: false,
      selectedEventTypes: [],
    }
  },
  computed: {
    ...mapGetters({
      group: 'getGroup',
    }),
    filterItems() {
      return Object.values(ClientHistoryEventTypes).map(value => ({
        text: this.$t(`client-history.list.${value.toLowerCase()}`),
        value,
      }))
    },
    filteredTableRows() {
      if (!this.selectedEventTypes.length) {
        return this.tableRows
      }

      return this.tableRows.filter(({event}) => this.selectedEventTypes.includes(event))
    },
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler() {
        this.initData()
        this.loadClientHistory()
      },
    },
    selectedEventTypes() {
      this.resetPaginationTrigger = !this.resetPaginationTrigger
    },
  },
  methods: {
    removePrefix(str) {
      return str.includes('-') ? str.substring(str.indexOf('-') + 1) : str
    },
    async loadClientHistory() {
      const {id: contactId} = this.$route.params

      if (contactId) {
        try {
          this.isLoading = true
          const [client, offers, loans, leads] = await Promise.all([
            this.getClient(contactId),
            this.getOffers(contactId),
            this.getLoansByContact(contactId),
            this.getLeadsByDealerContact(contactId),
          ])

          this.client = client

          this.tableRows = this.sortTableRows([...offers, ...loans, ...leads])
        } finally {
          this.isLoading = false
        }
      }
    },

    async reloadData(newLead) {
      this.isLoading = true
      await new Promise(resolve => setTimeout(resolve, 3000))
      await this.loadClientHistory()

      if (newLead && this.tableRows.find(entity => entity.id === newLead.id) === false) {
        this.tableRows = [mapLeadToClientHistory(newLead), ...this.tableRows]
      }
    },

    async getClient(contactId) {
      const res = await new DealersContactsApi().getContactById(contactId)

      return res.data || null
    },

    async getOffers(contactId) {
      const request = {
        dealer: {id: this.group.dealerId, code: this.group.code},
        pagination: {
          from: 0,
          size: 100,
        },
        params: {
          statuses: Object.values(Status),
        },
        contactId,
      }

      const response = await apiOffers.getOffersListByDealerId(request)

      return mapOffersToClientHistory(response.hits)
    },

    async getLoansByContact(contactId) {
      const res = await DealerBikesApi.getContactLoanList(contactId)

      return mapLoansToClientHistory(res.data.data)
    },

    async getLeadsByDealerContact(contactId) {
      const res = await ridoApi.getLeadsByDealerContact(contactId)

      return res.data.data.map(mapLeadToClientHistory)
    },

    handleLeadDialogOpenClose() {
      this.isLeadDialogOpen = !this.isLeadDialogOpen
    },

    async onCreateOfferMotorbike() {
      this.$store.commit('RESET_OFFER')
      this.client = {...this.client, gdprConsent: true, privacyTermsAccepted: true}

      this.$store.commit('SET_OFFER_SEARCHED_CONTACT', this.client)
      this.sendGAEvent('InitOfferClientHistory', 'Steps')

      await router.push({name: 'CreateOfferMotorbike'})
    },

    async refreshLoansList() {
      const loans = await this.getLoansByContact(this.$route.params.id)
      const newTableRows = [...this.tableRows.filter(({event}) => event !== ClientHistoryEventTypes.LOAN), ...loans]
      this.tableRows = this.sortTableRows(newTableRows)
    },

    handleRedirect(name) {
      if (name) {
        router.push({name})

        return
      }

      this.$router.back()
    },

    sortTableRows(rows) {
      return rows
        .map(item => ({...item, idWithoutPrefix: this.removePrefix(item.id)}))
        .toSorted((a, b) => (a.idWithoutPrefix > b.idWithoutPrefix ? -1 : 1))
    },

    async sendGAEvent(action, label) {
      await this.$gtm.trackEvent({category: 'Offers', action, label})
    },

    initData() {
      this.client = null
      this.tableRows = []
      this.isOfferDialogOpen = false
      this.isDealerDialogOpen = false
    },
  },
}
</script>

<style lang="scss" scoped>
.client-history {
  &__go-back-btn {
    background: transparent !important;

    i {
      padding-bottom: 2px;
    }
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
  }

  &__info {
    h2 {
      font-size: 40px;
      font-family: 'ProximaNovaExCnSBold', sans-serif;
      color: #dc182c;
      margin-bottom: 0;
    }
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 1rem;
    padding-top: 11px;
  }

  &__select {
    width: 305px;
  }
}

#client-history-card {
  padding: 24px 36px;
  border-radius: 9px;
}

#app button.v-btn--outlined {
  padding: 0 12px !important;
  border-color: #9a9a9a !important;
}
</style>
